import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { CheckCircle2, Database, User } from 'lucide-react';

const Installation = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    db_host: 'localhost',
    db_port: 27017,
    db_name: 'lead_management',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.setupInstallation(formData);
      toast.success('Installation completed successfully!');
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (error) {
      toast.error(error.message || 'Installation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3" data-testid="installation-title">
            Global CRM Software
          </h1>
          <p className="text-base text-slate-600">Setup wizard - Configure your system</p>
        </div>

        <Card className="glass" data-testid="installation-card">
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                  {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
                </div>
                <span className="text-sm font-medium">Database</span>
              </div>
              <div className="h-px bg-slate-300 flex-1 mx-4" />
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                  {step > 2 ? <CheckCircle2 className="w-5 h-5" /> : '2'}
                </div>
                <span className="text-sm font-medium">Admin Account</span>
              </div>
            </div>
            <CardTitle data-testid="step-title">{step === 1 ? 'Database Configuration' : 'Create Admin Account'}</CardTitle>
            <CardDescription>
              {step === 1 ? 'Configure your MongoDB database connection' : 'Set up the administrator account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {step === 1 && (
                <div className="space-y-4 animate-slide-in">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="db_host">Database Host</Label>
                      <Input
                        id="db_host"
                        data-testid="db-host-input"
                        value={formData.db_host}
                        onChange={(e) => setFormData({ ...formData, db_host: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="db_port">Port</Label>
                      <Input
                        id="db_port"
                        type="number"
                        data-testid="db-port-input"
                        value={formData.db_port}
                        onChange={(e) => setFormData({ ...formData, db_port: parseInt(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="db_name">Database Name</Label>
                    <Input
                      id="db_name"
                      data-testid="db-name-input"
                      value={formData.db_name}
                      onChange={(e) => setFormData({ ...formData, db_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button type="button" onClick={() => setStep(2)} data-testid="next-step-btn">
                      Next <Database className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4 animate-slide-in">
                  <div>
                    <Label htmlFor="admin_name">Full Name</Label>
                    <Input
                      id="admin_name"
                      data-testid="admin-name-input"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_email">Email</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      data-testid="admin-email-input"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_phone">Phone (with country code)</Label>
                    <Input
                      id="admin_phone"
                      placeholder="+919876543210"
                      data-testid="admin-phone-input"
                      value={formData.admin_phone}
                      onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_password">Password</Label>
                    <Input
                      id="admin_password"
                      type="password"
                      data-testid="admin-password-input"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex justify-between">
                    <Button type="button" variant="outline" onClick={() => setStep(1)} data-testid="back-btn">
                      Back
                    </Button>
                    <Button type="submit" disabled={loading} data-testid="complete-installation-btn">
                      {loading ? 'Installing...' : 'Complete Installation'} <User className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Installation;
